package components.productItem

import common.EColors
import common.ECssClasses
import components.Spacer.Spacer
import components.colourToneItem.EColourToneItemLeftButtonType
import components.toast.ToastItem
import core.AppContext
import core.enums.EImages
import core.enums.EJSBridgeActions
import core.enums.ELocalizationKeys
import core.extensions.isInNativeApp
import core.extensions.postMessageToNativeApp
import core.extensions.toClassName
import core.extensions.toColor
import core.io.dto.ExternalLinkDTO
import core.io.dto.JSBridgeActionDTO
import core.localStorage.LocalStorageDAO
import core.localization.Localization
import core.piwik.Piwik
import core.util.ColourHelper
import core.util.QuantityCalculator
import csstype.AlignItems
import csstype.Auto
import csstype.BackgroundColor
import csstype.Border
import csstype.Cursor
import csstype.Display
import csstype.FlexDirection
import csstype.FlexWrap
import csstype.JustifyContent
import csstype.LineStyle
import csstype.NamedColor
import csstype.ObjectFit
import csstype.Position
import csstype.TextAlign
import csstype.TextDecorationLine
import csstype.integer
import csstype.number
import csstype.pct
import csstype.px
import csstype.rgba
import emotion.react.css
import kotlinx.browser.window
import module.watchlist.ColourListObject
import org.w3c.dom.HTMLInputElement
import react.FC
import react.PropsWithClassName
import react.dom.html.InputType
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.span
import react.router.Navigate
import react.useContext
import react.useRef
import react.useState
import screens.EScreens
import setUnsafeInnerHTML

/**
 * Copyright (c) emgress GmbH. All rights reserved.
 *
 * Created on : 09.11.22
 * Author : mariorosshirt
 */

external interface ProductItemProps : PropsWithClassName {
    var mColourObject: ColourListObject
    var mDeleteFunction: () -> Unit
    var mColourToneItemLeftButtonType: EColourToneItemLeftButtonType?
    var mColourToneItemLeftButtonToastMessage: (toastItem: ToastItem?) -> Unit
    var mNavigate: String?
    var mEmptyEanFunction: () -> Unit
}

val ProductItem = FC<ProductItemProps> { props ->

    val mQuantityCalculator = QuantityCalculator()

    val mContext by useContext(AppContext)
    var (mLocalStorage, mLocalStorageDispatch) = mContext.mLocalStorageReducer
    val mColourObject = props.mColourObject

    val mIsColourBlack = ColourHelper.getContrastIsBlack(mColourObject.mColourCode)
    val mContrastColour = if (mIsColourBlack) NamedColor.black else NamedColor.white
    val mColourToneItemLeftButtonType = props.mColourToneItemLeftButtonType ?: EColourToneItemLeftButtonType.WATCH_LIST

    val mOnWatchlist = mLocalStorage.mWatchlist.any { it.mUnique == mColourObject.mUnique }
    val mOnProductList = mLocalStorage.mProductList.any { it.mUnique == mColourObject.mUnique }

    val numberInputRef = useRef<HTMLInputElement>(null)
    var squareMeterTextInput: String by useState(mColourObject.mSquareMeter?.toString() ?: "")

    var mSpeechBubbleVisible by useState(false)

    var navigate: String? by useState(props.mNavigate)

    fun onCardClick() {
        mLocalStorageDispatch.invoke(
            LocalStorageDAO.ELocalStorageAction.ADD_DETAIL_COLOUR_PRODUCT(mColourObject)
        )
        navigate = EScreens.COLOUR_PRODUCT_DETAIL_SCREEN.mPath
    }
    div {
        css(ECssClasses.PRODUCT_CARD_ITEM.toClassName(), props.className) {
            backgroundColor = mColourObject.mColourCode.unsafeCast<BackgroundColor>()
            minHeight = 144.px
            borderRadius = 4.px
        }
        onClick = {
            onCardClick()
        }
        div {
            css {
                flexGrow = number(1.0)
                display = Display.flex
                flexDirection = FlexDirection.row
                justifyContent = JustifyContent.flexStart
            }

            div {
                css {
                    height = 72.px
                    minWidth = 72.px
                    marginLeft = 8.px
                    marginTop = 8.px
                    borderRadius = 4.px
                    backgroundColor = NamedColor.white
                    justifyContent = JustifyContent.center
                    display = Display.flex
                    alignItems = AlignItems.center
                }
                img {
                    css {
                        height = 41.px
                        width = 47.px
                        objectFit = ObjectFit.contain
                    }
                    src = mColourObject.mImageUrl
                }
            }

            div {
                css {
                    marginBottom = 3.px
                    marginTop = 8.px
                    marginLeft = 10.px
                    display = Display.flex
                    flexDirection = FlexDirection.column
                    justifyContent = JustifyContent.spaceBetween
                    flexGrow = number(1.0)
                }
                div {
                    css {
                        display = Display.flex
                        flexDirection = FlexDirection.row
                        flexWrap = FlexWrap.wrapReverse
                        flexGrow = number(1.0)
                    }

                    span {
                        css(ECssClasses.PRODUCT_CARD_ITEM_SMALL_FONT.toClassName()) {
                            color = mContrastColour
                            zIndex = integer(2)
                            marginBottom = Auto.auto
                        }
                        +mColourObject.mColourCollection
                    }
                    div {
                        css {
                            display = Display.flex
                            flexGrow = number(1.0)
                        }
                        Spacer()
                        div {
                            css(ECssClasses.CURSOR_HOVER.toClassName()) {
                                marginRight = 36.px
                            }
                            img {
                                css {
                                    minHeight = 24.px
                                }
                                src = when (mColourToneItemLeftButtonType) {
                                    EColourToneItemLeftButtonType.WATCH_LIST -> EImages.WATCHLIST_ICON(
                                        EImages.EColor.fromColorHex(
                                            mColourObject.mColourCode
                                        ), EImages.EType.fromIsOnList(mOnWatchlist)
                                    ).mRelativePath

                                    EColourToneItemLeftButtonType.PRODUCT_LIST -> EImages.PAINT_BUCKET(
                                        EImages.EColor.fromColorHex(
                                            mColourObject.mColourCode
                                        ), EImages.EType.fromIsOnList(mOnProductList)
                                    ).mRelativePath
                                }
                            }
                            onClick = {
                                when (mColourToneItemLeftButtonType) {
                                    EColourToneItemLeftButtonType.WATCH_LIST -> {
                                        mLocalStorageDispatch(
                                            LocalStorageDAO.ELocalStorageAction.ADD_OR_DELETE_FROM_WATCHLIST(
                                                mColourObject
                                            )
                                        ).also {
                                            props.mColourToneItemLeftButtonToastMessage(
                                                if (mLocalStorage.mWatchlist.contains(mColourObject)) {
                                                    ToastItem(Localization.getLocalizationKey(ELocalizationKeys.CORE_TOAST_DELETE_FROM_WATCH_LIST))
                                                } else {
                                                    ToastItem(Localization.getLocalizationKey(ELocalizationKeys.CORE_TOAST_ADD_TO_WATCH_LIST))
                                                }
                                            )
                                            if (mLocalStorage.mWatchlist.contains(mColourObject).not()) {
                                                Piwik.trackContentInteraction(
                                                    Piwik.CATEGORY_WISH_LIST,
                                                    Piwik.NAME_WISHLIST_COLOR_ADD,
                                                    mColourObject.mUnique,
                                                    Piwik.TARGET_COLOR
                                                )
                                            }
                                        }
                                    }

                                    EColourToneItemLeftButtonType.PRODUCT_LIST -> {
                                        mLocalStorageDispatch(
                                            LocalStorageDAO.ELocalStorageAction.ADD_OR_DELETE_FROM_PRODUCTLIST(
                                                mColourObject
                                            )
                                        ).also {
                                            props.mColourToneItemLeftButtonToastMessage(
                                                if (mLocalStorage.mProductList.contains(mColourObject)) {
                                                    ToastItem(Localization.getLocalizationKey(ELocalizationKeys.CORE_TOAST_DELETE_FROM_PRODUCT_LIST.name))
                                                } else {
                                                    ToastItem(Localization.getLocalizationKey(ELocalizationKeys.CORE_TOAST_ADD_TO_PRODUCT_LIST.name))
                                                }
                                            )
                                            if (mLocalStorage.mWatchlist.contains(mColourObject).not()) {
                                                Piwik.trackContentInteraction(
                                                    Piwik.CATEGORY_PRODUCT_LIST,
                                                    Piwik.NAME_PRODUCTLIST_COLOR_ADD,
                                                    mColourObject.mUnique,
                                                    Piwik.TARGET_COLOR
                                                )
                                            }
                                        }
                                    }
                                }
                                it.stopPropagation()
                            }
                        }
                        div {
                            css(ECssClasses.CURSOR_HOVER.toClassName()) {
                                marginRight = 16.px
                            }
                            img {
                                css {
                                    minHeight = 24.px
                                }
                                src =
                                    if (mIsColourBlack) EImages.DELETE_ICON_BLACK.mRelativePath else EImages.DELETE_ICON_WHITE.mRelativePath
                            }
                            onClick = {
                                props.mDeleteFunction()
                                it.stopPropagation()
                            }
                        }
                    }

                }
                span {
                    css(ECssClasses.PRODUCT_CARD_ITEM_MEDIUM_FONT_WEIGHTED.toClassName()) {
                        color = mContrastColour
                    }
                    setUnsafeInnerHTML("<b>${mColourObject.mArtNr}</b>")
                }
                span {
                    css(ECssClasses.PRODUCT_CARD_ITEM_MEDIUM_FONT.toClassName()) {
                        color = mContrastColour
                    }
                    if(mColourObject.mName != ""){
                        setUnsafeInnerHTML(mColourObject.mName)
                    }else{
                        setUnsafeInnerHTML("</br>")
                    }
                }
            }

            /* Icon Div
            div {
                css {
                    marginLeft = Auto.auto
                    position = Position.absolute
                    zIndex = integer(2)
                    right = 0.px
                    marginRight = 24.px
                    marginTop = 4.px
                }

            }*/
        }

        div {
            css {
                borderWidth = 1.px
                borderColor = mContrastColour
                borderStyle = LineStyle.solid
                backgroundColor = mContrastColour
                marginTop = 6.px
                marginBottom = 8.px
                marginLeft = 8.px
                marginRight = 8.px
            }
        }
        div {
            css {
                display = Display.flex
                flexDirection = FlexDirection.row
                justifyContent = JustifyContent.spaceBetween

            }
            div {
                css {
                    display = Display.flex
                    position = Position.relative
                    marginBottom = 8.px
                    marginLeft = 8.px
                    alignItems = AlignItems.center
                }
                div {
                    css {
                        borderRadius = 50.pct
                        width = 24.px
                        height = 24.px
                        backgroundColor = NamedColor.white
                        color = NamedColor.black
                        position = Position.absolute
                        display = Display.flex
                        alignItems = AlignItems.center
                        justifyContent = JustifyContent.spaceAround
                        marginLeft = 4.px
                        marginRight = 4.px
                    }
                    onClick = {
                        mSpeechBubbleVisible = true
                        it.stopPropagation()
                    }
                    if (mSpeechBubbleVisible) {
                        div {
                            css(ECssClasses.SPEECHBUBBLE.toClassName()) {
                                top = 36.px
                                left = (-30).px
                                width = 246.px
                            }
                            setUnsafeInnerHTML(Localization.getLocalizationKey(ELocalizationKeys.S05_QUESTION_MARK_TEXT.name))
                        }
                        div {
                            css(ECssClasses.SPEECHBUBBLE_HIDER.toClassName()) {

                            }
                            onClick = {
                                mSpeechBubbleVisible = false
                                it.stopPropagation()
                            }
                        }
                    }
                    +"?"
                }
                input {
                    css(ECssClasses.PRODUCT_CARD_ITEM_MEDIUM_FONT_WEIGHTED.toClassName()) {
                        height = 40.px
                        maxWidth = 186.px
                        textAlign = TextAlign.end
                        paddingRight = 36.px
                        width = 100.pct
                        borderWidth = 0.px
                        backgroundColor = rgba(255, 255, 255, 0.5)
                        borderRadius = 4.px
                        border = Border(1.px, LineStyle.solid, EColors.SECONDARY_BUTTON.toColor())
                    }
                    id = "inputNumber"
                    type = InputType.text
                    name = "inputNumber"
                    ref = numberInputRef
                    onChange = {
                        squareMeterTextInput = it.target.value
                    }
                    value = squareMeterTextInput
                    placeholder = Localization.getLocalizationKey(ELocalizationKeys.S05_PRODUCT_LIST_TEXT_PLACEHOLDER)
                    onBlur = {
                        mColourObject.mSquareMeter = squareMeterTextInput.replace(",", ".").toDouble()
                        mLocalStorageDispatch.invoke(
                            LocalStorageDAO.ELocalStorageAction.UPDATE_COLOR_IN_PRODUCTLIST(
                                mColourObject
                            )
                        )
                        Piwik.trackCustomEvent(
                            Piwik.CATEGORY_PRODUCT_LIST,
                            Piwik.NAME_PRODUCTLIST_USE_AMOUNT_CALCULATOR,
                            Piwik.NAME_PRODUCTLIST_USE_AMOUNT_CALCULATOR
                        )
                    }
                    onClick = {
                        it.stopPropagation()
                    }
                }
                if (squareMeterTextInput != "") {
                    span {
                        css(ECssClasses.PRODUCT_CARD_ITEM_MEDIUM_FONT_WEIGHTED.toClassName()) {
                            position = Position.absolute
                            top = 10.5.px
                            right = 10.px
                        }
                        +"m²"
                    }
                }
            }
            div {
                css {
                    marginRight = 8.px
                    display = Display.flex
                    flexDirection = FlexDirection.column
                    textAlign = TextAlign.end
                }
                span {
                    css(ECssClasses.PRODUCT_CARD_ITEM_SMALL_FONT.toClassName()) {
                        color = mContrastColour
                    }
                    setUnsafeInnerHTML(Localization.getLocalizationKey(ELocalizationKeys.S05_PRODUCT_LIST_NEEDED_AMOUNT))
                }
                span {
                    css(ECssClasses.PRODUCT_CARD_ITEM_AMOUNT_FONT.toClassName()) {
                        color = mContrastColour
                    }
                    mQuantityCalculator.calculateQuantity(squareMeterTextInput, mColourObject.mColourCollection)?.let {
                        setUnsafeInnerHTML("$it ${Localization.getLocalizationKey(ELocalizationKeys.S05_PRODUCT_LIST_NEEDED_AMOUNT_UNIT)}")
                    } ?: run {
                        setUnsafeInnerHTML("-")
                    }
                }
            }
        }
        div{
            css(ECssClasses.PRODUCT_CARD_ITEM_MEDIUM_FONT_WEIGHTED.toClassName()) { height = 16.px
                textAlign = TextAlign.center
                textDecorationLine = TextDecorationLine.underline
                marginBottom = 16.px
                color = if(mIsColourBlack) NamedColor.black else NamedColor.white
            }
            span{
                css {
                    cursor = Cursor.pointer
                }
                +Localization.getLocalizationKey(ELocalizationKeys.BUY_OPTIONS.name)
            }
            onClick = {
                if(mColourObject.mEanCode.isEmpty()){
                    Piwik.trackContentInteraction(
                        Piwik.CATEGORY_COMMERCE_CONNECTOR,
                        Piwik.NAME_CC_OPEN_EMPTY_EAN,
                        mColourObject.mUnique,
                        Piwik.TARGET_COMMERCE
                    )
                    props.mEmptyEanFunction()
                } else {
                    Piwik.trackContentInteraction(
                        Piwik.CATEGORY_COMMERCE_CONNECTOR,
                        Piwik.NAME_CC_OPEN,
                        mColourObject.mUnique,
                        Piwik.TARGET_COMMERCE
                    )
                    if(!window.isInNativeApp()){
                        window.open(
                            Localization.getLocalizationKey(ELocalizationKeys.BUY_OPTIONS_URL.name)
                                .plus(mColourObject.mEanCode)
                        )
                    }else{
                        val jsBridgeActionDTO = JSBridgeActionDTO(
                            EJSBridgeActions.EXTERNAL_LINK,
                            ExternalLinkDTO(
                                Localization.getLocalizationKey(ELocalizationKeys.BUY_OPTIONS_URL.name)
                                    .plus(mColourObject.mEanCode)
                            )
                        )
                        window.postMessageToNativeApp(jsBridgeActionDTO)
                    }
                }
                it.stopPropagation()
            }
        }
    }
    navigate?.let {
        Navigate {
            to = it
        }
    }
}